import {
  INSIGHT_ANSWERS_CREATE,
  INSIGHT_DETAIL,
  INSIGHT_LINK_CREATE,
  INSIGHT_LIST,
  INSIGHT_SHARE,
  MATERIALITY_ASSESSMENT_DASHBOARD, MATERIALITY_ASSESSMENT_DASHBOARD_TOPICS,
  QUESTIONS_DETAIL,
  QUESTIONS_DUPLICATE,
  QUESTIONS_LIST,
  RESPONDENT_IMPACT,
  STANDARDS,
  STANDARDS_TOPICS,
} from 'constants/requestUrls';
import mainApi from 'store/apis/mainApi';
import RESPONDENT_IMPACT_TAG from 'store/tags/respondentImpact';
import SURVEY_LIST_TAG from 'store/tags/survey';

import { API_METHODS } from '../../../../constants/general';

import type { IBaseQueryPaginationResponse } from 'store/types/IBaseQueryPaginationResponse';
import type IBaseQueryResponse from '../../../types/IBaseQueryResponse';
import type { IDashboardTopic } from '../interfaces/IDashboardTopicsResponse';
import type { ICreateInsightAnswersRequest } from '../interfaces/IInsightAnswersRequest';
import type { IInsightsDashboardRequest } from '../interfaces/IInsightsDashboardRequest';
import type {
  IInsightsDashboardResponse,
} from '../interfaces/IInsightsDashboardResponse';
import type IQuestionsItem from '../interfaces/IQuestionsItem';
import type { IRespondentImpactItem } from '../interfaces/IRespondentImpactItem';
import type { ISetRespondentImpactRequest } from '../interfaces/ISetRespondentImpactRequest';
import type ISingleInsightItem from '../interfaces/ISingleInsightItem';
import type ISingleSurveyRequest from '../interfaces/ISingleSurveyRequest';
import type IStandardTopicsItem from '../interfaces/IStandardTopicsItem';
import type IStandardTopicsRequest from '../interfaces/IStandardTopicsRequest';
import type ISurveyInsightItem from '../interfaces/ISurveyInsightItem';
import type ISurveyListRequest from '../interfaces/ISurveyListRequest';
import type {
  ICreateSurveyRequest, ILinkCreateRequest, ILinkCreateResponse, IUpdateSurveyRequest,
} from '../interfaces/ISurveyRequests';
import type IShareInsightRequest from '../interfaces/ISurveyRequests';
import type ISurveyStandardsRequest from '../interfaces/ISurveyStandartsRequest';

export const surveyApi = mainApi.injectEndpoints({
  endpoints: (build) => ({
    getSurveys: build.query<IBaseQueryPaginationResponse<IQuestionsItem[]>, ISurveyListRequest>({
      query: ({
        offset, orderBy, maId, status, limit,
      }) => ({
        url: QUESTIONS_LIST,
        params: {
          ...(offset ? { offset } : { offset: 0 }),
          ...(orderBy && { orderBy }),
          ...(maId && { maId }),
          ...(status && { status }),
          ...(limit && { limit }),
        },
      }),
      providesTags: (result) => (
        result?.data?.items?.length && result?.data?.items?.length > 0
          ? [...result.data.items.map(({ id }) => (
            { type: SURVEY_LIST_TAG, id } as const)), SURVEY_LIST_TAG]
          : [SURVEY_LIST_TAG]
      ),
    }),
    getSingleSurvey: build.query<IBaseQueryResponse<IQuestionsItem>, ISingleSurveyRequest>({
      query: ({ id }) => ({
        url: QUESTIONS_DETAIL.replace('{id}', `${id}`),
      }),
      providesTags: (result) => (
        result?.data?.id
          ? [{ type: SURVEY_LIST_TAG, id: result.data?.id }]
          : [SURVEY_LIST_TAG]
      ),
    }),
    createSurvey: build.mutation<IBaseQueryResponse<IQuestionsItem>, ICreateSurveyRequest>({
      query: (data) => ({
        url: QUESTIONS_LIST,
        method: API_METHODS.post,
        body: data,
      }),
      invalidatesTags: [SURVEY_LIST_TAG],
    }),
    updateSurvey: build.mutation<IBaseQueryResponse<IQuestionsItem>, IUpdateSurveyRequest>({
      query: ({ id, ...data }) => ({
        url: QUESTIONS_DETAIL.replace('{id}', `${id}`),
        method: API_METHODS.patch,
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        SURVEY_LIST_TAG,
        { type: SURVEY_LIST_TAG, id },
      ],
    }),
    deleteSurvey: build.mutation<IBaseQueryResponse<string>, { id: number }>({
      query: ({ id }) => ({
        url: QUESTIONS_DETAIL.replace('{id}', `${id}`),
        method: API_METHODS.delete,
      }),
      invalidatesTags: [SURVEY_LIST_TAG],
    }),
    getStandards: build.query<IBaseQueryResponse<ISurveyStandardsRequest>, ISurveyStandardsRequest>({
      query: () => ({
        url: STANDARDS,
      }),
      providesTags: (result) => (
        result?.data?.items?.length && result?.data?.items?.length > 0
          ? result.data.items.map((item) => ({ type: SURVEY_LIST_TAG, id: item.id }))
          : []
      ),
    }),
    duplicateSurvey: build.mutation<IBaseQueryResponse<IQuestionsItem>, { id: number }>({
      query: ({ id }) => ({
        url: QUESTIONS_DUPLICATE.replace('{id}', `${id}`),
        method: API_METHODS.post,
      }),
      invalidatesTags: [SURVEY_LIST_TAG],
    }),
    getInsight: build.query<IBaseQueryPaginationResponse<ISurveyInsightItem[]>, ISurveyListRequest>({
      query: ({
        offset, limit, orderBy, insightType, maId, valueChainMapId,
      }) => ({
        url: INSIGHT_LIST,
        params: {
          ...(offset !== undefined ? { offset } : { offset: 0 }),
          ...(limit !== undefined ? { limit } : { limit: 50 }),
          ...(orderBy && { orderBy }),
          ...(insightType && { insightType }),
          ...(maId && { maId }),
          ...(valueChainMapId && { valueChainMapId }),
        },
      }),
      providesTags: [SURVEY_LIST_TAG],
    }),
    getSingleInsight: build.query<IBaseQueryResponse<ISingleInsightItem>, ISingleSurveyRequest>({
      query: ({ id }) => ({
        url: INSIGHT_DETAIL.replace('{id}', `${id}`),
      }),
      providesTags: (result) => (
        result?.data?.id
          ? [{ type: SURVEY_LIST_TAG, id: result.data?.id }]
          : [SURVEY_LIST_TAG]
      ),
    }),
    deleteInsight: build.mutation<IBaseQueryResponse<string>, { id: number }>({
      query: ({ id }) => ({
        url: INSIGHT_DETAIL.replace('{id}', `${id}`),
        method: API_METHODS.delete,
      }),
      invalidatesTags: [SURVEY_LIST_TAG],
    }),
    createInsightAnswers: build.mutation<IBaseQueryResponse<any>, ICreateInsightAnswersRequest>({
      query: (data) => ({
        url: INSIGHT_ANSWERS_CREATE,
        method: API_METHODS.post,
        body: data,
      }),
    }),
    shareInsight: build.mutation<IBaseQueryResponse<any>, IShareInsightRequest>({
      query: (data) => ({
        url: INSIGHT_SHARE,
        method: API_METHODS.post,
        body: data,
      }),
    }),
    linkCreate: build.mutation<IBaseQueryResponse<ILinkCreateResponse>, ILinkCreateRequest>({
      query: ({ surveyToken, respondentId }) => ({
        url: INSIGHT_LINK_CREATE,
        method: API_METHODS.post,
        params: { token: surveyToken },
        body: { respondentId },
      }),
    }),
    getStandardTopics: build.query<IStandardTopicsItem[], IStandardTopicsRequest>({
      query: ({
        offset, limit, orderBy, standardId,
      }) => ({
        url: STANDARDS_TOPICS,
        params: {
          ...(offset !== undefined ? { offset } : { offset: 0 }),
          ...(limit !== undefined ? { limit } : { limit: 50 }),
          ...(orderBy && { orderBy }),
          ...(standardId && { standardId }),
        },
      }),
      transformResponse: (response: IBaseQueryPaginationResponse<IStandardTopicsItem[]>) => response.data?.items || [],
      providesTags: (result) => (
        result && result.length > 0
          ? [...result.map(({ id }) => (
            { type: SURVEY_LIST_TAG, id } as const)), SURVEY_LIST_TAG]
          : [SURVEY_LIST_TAG]
      ),
    }),
    getInsightsDashboard: build.query<IInsightsDashboardResponse, IInsightsDashboardRequest>({
      query: ({
        questionListId, topicId, companyId, respondentType, valueChainMapId,
      }) => ({
        url: MATERIALITY_ASSESSMENT_DASHBOARD,
        params: {
          questionListId,
          ...(topicId !== undefined ? { topicId } : {}),
          ...(companyId !== undefined ? { companyId } : {}),
          ...(respondentType && respondentType.length ? { respondentType } : {}),
          ...(valueChainMapId ? { valueChainMapId } : { }),
        },
      }),
      transformResponse: (response: any) => response.data || {},
    }),
    getDashboardTopics: build.query<IDashboardTopic[], { questionListId: number }>({
      query: ({ questionListId }) => ({
        url: `${MATERIALITY_ASSESSMENT_DASHBOARD_TOPICS}/${questionListId}`,
      }),
      transformResponse: (response: any) => response?.data?.topics.map((item: any) => ({
        id: item.id,
        value: item.name,
        label: item.name,
        ...item,
      })) || [],
    }),
    getRespondentImpact: build.query<IRespondentImpactItem[], { companyId?: number }>({
      query: ({ companyId } = {}) => ({
        url: RESPONDENT_IMPACT,
        params: companyId ? { companyId } : {},
      }),
      transformResponse: (response: any) => (Array.isArray(response.data) ? response.data : []),
      providesTags: [RESPONDENT_IMPACT_TAG],
    }),
    setRespondentImpact: build.mutation<IBaseQueryResponse<any>, ISetRespondentImpactRequest>({
      query: (data) => ({
        url: RESPONDENT_IMPACT,
        method: API_METHODS.patch,
        body: data,
      }),
      invalidatesTags: [RESPONDENT_IMPACT_TAG],
    }),
  }),
});

export const {
  useGetSurveysQuery,
  useGetSingleSurveyQuery,
  useCreateSurveyMutation,
  useUpdateSurveyMutation,
  useDeleteSurveyMutation,
  useDuplicateSurveyMutation,
  useGetStandardsQuery,
  useGetInsightQuery,
  useSetRespondentImpactMutation,
  useDeleteInsightMutation,
  useCreateInsightAnswersMutation,
  useShareInsightMutation,
  useLinkCreateMutation,
  useGetStandardTopicsQuery,
  useLazyGetInsightsDashboardQuery,
  useGetDashboardTopicsQuery,
  useGetRespondentImpactQuery,
} = surveyApi;
